import React, { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Color mapping for action buttons
const getButtonColorClasses = (color) => {
  const colorMap = {
    blue: 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-500',
    red: 'bg-red-500 hover:bg-red-600 focus:ring-red-500',
    green: 'bg-green-500 hover:bg-green-600 focus:ring-green-500',
    yellow: 'bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500',
    purple: 'bg-purple-500 hover:bg-purple-600 focus:ring-purple-500',
    gray: 'bg-gray-500 hover:bg-gray-600 focus:ring-gray-500',
    indigo: 'bg-indigo-500 hover:bg-indigo-600 focus:ring-indigo-500',
  };
  return colorMap[color] || colorMap.blue; // Default to blue if color not found
};

// SortableRow component
const SortableRow = ({ row, columns, actions, enableRowSelection, selected, onToggleSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    position: isDragging ? 'relative' : 'static',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      <td className="px-4 py-2 cursor-move" {...attributes} {...listeners}>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      </td>
      {enableRowSelection && (
        <td className="px-4 py-2">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(row._id)}
          />
        </td>
      )}
      {columns.map((column) => (
        <td key={column.key} className="px-4 py-2">
          {column.render ? column.render(row[column.key], row) : row[column.key]}
        </td>
      ))}
      {actions && (
        <td className="px-4 py-2">
          <div className="flex items-center gap-2">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={() => action.onClick(row._id)}
                className={`
                  inline-flex items-center justify-center
                  px-3 py-1.5 
                  text-sm font-medium text-white 
                  rounded-md
                  transition-colors duration-200
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  ${getButtonColorClasses(action.color)}
                `}
              >
                {action.label}
              </button>
            ))}
          </div>
        </td>
      )}
    </tr>
  );
};

const DndTable = ({
  items,
  columns,
  actions,
  onDragEnd,
  className = "",
  searchKey = 'label',
  onSearch,
  searchDelay = 500,
  searchValue = '',
  enableRowSelection = false,
  bulkActions = [],
}) => {
  const [searchTerm, setSearchTerm] = useState(searchValue);
  const [selectedIds, setSelectedIds] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  // Filter items based on search term (only if onSearch is not provided)
  const filteredItems = onSearch ? items : items.filter(item => {
    if (!searchTerm) return true;
    const searchKeys = Array.isArray(searchKey) ? searchKey : [searchKey];
    return searchKeys.some(key => {
      const value = item[key];
      return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });
  });

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // If onSearch is provided, call it with the search value
    if (onSearch) {
      onSearch(value);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleToggleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? filteredItems.map((item) => item._id) : []);
  };

  const clearSelection = () => setSelectedIds([]);

  return (
    <div className="flex flex-col w-full">
      {/* Bulk actions toolbar */}
      {enableRowSelection && selectedIds.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
          <div className="text-sm text-gray-700">{selectedIds.length} selected</div>
          <div className="flex items-center gap-2">
            {bulkActions.map((action, index) => (
              <button
                key={index}
                onClick={() => action.onClick(selectedIds, clearSelection)}
                className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${getButtonColorClasses(action.color)}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Search Input */}
      <div className="flex justify-end items-center px-4 py-2">
        <div className="relative w-64">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search..."
            className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* Table */}
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <table className={`min-w-full bg-white border border-gray-200 ${className}`}>
        <thead>
          <tr>
            <th className="px-4 py-2 text-left bg-gray-50 border-b">

            </th>
            {enableRowSelection && (
              <th className="px-4 py-2 text-left bg-gray-50 border-b">
                <input
                  type="checkbox"
                  checked={selectedIds.length > 0 && selectedIds.length === filteredItems.length}
                  onChange={handleToggleSelectAll}
                />
              </th>
            )}
            {columns.map((column) => (
              <th key={column.key} className="px-4 py-2 text-left bg-gray-50 border-b">
                {column.label}
              </th>
            ))}
            {actions && (
              <th className="px-4 py-2 text-left bg-gray-50 border-b">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
            <SortableContext items={filteredItems.map(item => item._id)} strategy={verticalListSortingStrategy}>
              {filteredItems.map((item) => (
              <SortableRow
                key={item._id}
                row={item}
                columns={columns}
                actions={actions}
                enableRowSelection={enableRowSelection}
                selected={selectedIds.includes(item._id)}
                onToggleSelect={handleToggleSelect}
              />
            ))}
          </SortableContext>
        </tbody>
      </table>
    </DndContext>
    </div>
  );
};

export default DndTable; 